import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';
import { ArrowRight, TrendingUp, TrendingDown, X, CalendarIcon, Loader2, Clock, PhoneOff, Calendar as CalendarIcon2, Timer, AlertTriangle, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { SOURCE_TYPES, CHANNEL_TYPES } from '@/lib/sourceChannelTypes';
import { getLocalDayStart, getLocalDayEnd, getLocalWeekStart, getLocalMonthStart } from '@/lib/timezoneUtils';
import DealsDetailModal from './DealsDetailModal';
import DealAgingChart from './DealAgingChart';
import DealAgingList from './DealAgingList';
import DealsWithoutScheduledCallsChart from './DealsWithoutScheduledCallsChart';
import DealAgingByCreationList from './DealAgingByCreationList';
import FunnelAnalyticsSection from './FunnelAnalyticsSection';
import { usePipelineMetrics } from '@/hooks/usePipelineMetrics';
import { usePipelineDealsDetail } from '@/hooks/usePipelineDealsDetail';
import { useDealAging } from '@/hooks/useDealAging';
import { useDealsWithoutScheduledCalls } from '@/hooks/useDealsWithoutScheduledCalls';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
const STAGE_ORDER = ['new', 'contact', 'scheduled', 'proposal', 'negotiation', 'won', 'lost'];

export default function PipelineMetricsRPC({ 
  users = [],
  consultantFilter = 'all',
  onConsultantFilterChange,
  sdrFilter = 'all',
  onSdrFilterChange,
  sourceFilter = 'all',
  onSourceFilterChange,
  channelFilter = 'all',
  onChannelFilterChange,
  createdDateFilter = 'all',
  onCreatedDateFilterChange,
  createdDateRange = null,
  onCreatedDateRangeChange,
  businessUnitFilter = 'all',
}) {
  const { t } = useTranslation();

  const CREATED_DATE_OPTIONS = [
    { value: 'all', label: t('pipelineMetrics.dateOptions.allDates') },
    { value: 'today', label: t('pipelineMetrics.dateOptions.today') },
    { value: 'yesterday', label: t('pipelineMetrics.dateOptions.yesterday') },
    { value: 'this_week', label: t('pipelineMetrics.dateOptions.thisWeek') },
    { value: 'this_month', label: t('pipelineMetrics.dateOptions.thisMonth') },
    { value: 'custom', label: t('pipelineMetrics.dateOptions.customRange') }
  ];

  const STAGE_CONFIG = {
    new: { label: t('pipelineMetrics.stages.new'), color: 'hsl(var(--chart-1))' },
    contact: { label: t('pipelineMetrics.stages.contact'), color: 'hsl(var(--chart-2))' },
    scheduled: { label: t('pipelineMetrics.stages.scheduled'), color: 'hsl(var(--chart-3))' },
    proposal: { label: t('pipelineMetrics.stages.proposal'), color: 'hsl(var(--chart-4))' },
    negotiation: { label: t('pipelineMetrics.stages.negotiation'), color: 'hsl(var(--chart-5))' },
    won: { label: t('pipelineMetrics.stages.won'), color: 'hsl(var(--success))' },
    lost: { label: t('pipelineMetrics.stages.lost'), color: 'hsl(var(--destructive))' },
  };

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalSubtitle, setModalSubtitle] = useState('');
  const [selectedStage, setSelectedStage] = useState(null);
  const [modalDeals, setModalDeals] = useState([]);

  // Build filters for RPC
  const rpcFilters = useMemo(() => ({
    consultantId: consultantFilter !== 'all' && consultantFilter !== 'unassigned' ? consultantFilter : null,
    sdrId: sdrFilter !== 'all' && sdrFilter !== 'unassigned' ? sdrFilter : null,
    source: sourceFilter !== 'all' ? sourceFilter : null,
    channel: channelFilter !== 'all' ? channelFilter : null,
    createdFrom: createdDateRange?.from || null,
    createdTo: createdDateRange?.to || null,
    businessUnit: businessUnitFilter !== 'all' ? businessUnitFilter : null,
  }), [consultantFilter, sdrFilter, sourceFilter, channelFilter, createdDateRange, businessUnitFilter]);

  // Fetch metrics using RPC
  const { data: metrics, isLoading, error } = usePipelineMetrics(rpcFilters);

  // Fetch deals detail when modal opens (for stage clicks)
  const { data: stageDealsData } = usePipelineDealsDetail({
    ...rpcFilters,
    stage: selectedStage,
    limit: 200,
  }, modalOpen && !!selectedStage);

  // Query client for manual refetch
  const queryClient = useQueryClient();

  // Fetch deals for aging hooks (client-side query needed for aging analysis)
  const { 
    data: dealsForAging = [], 
    isLoading: dealsLoading,
    error: dealsForAgingError,
    isFetching: dealsForAgingFetching,
  } = useQuery({
    queryKey: ['deals-for-aging', rpcFilters],
    queryFn: async () => {
      let query = supabase
        .from('deals')
        .select(`
          id, name, stage, total_amount, created_at, closed_date, 
          sales_consultant, sales_development, contact_id, deals_source, deals_channel,
          deal_status, cancelled_at, upsell_credit_amount,
          contact:contacts!fk_deals_contact(id, first_name, last_name),
          sales_consultant_user:app_users!sales_consultant(id, display_name, first_name, last_name),
          sales_development_user:app_users!sales_development(id, display_name, first_name, last_name)
        `)
        .not('stage', 'in', '("won","lost")')
        .order('created_at', { ascending: false });

      // Apply filters
      if (rpcFilters.consultantId) {
        query = query.eq('sales_consultant', rpcFilters.consultantId);
      }
      if (rpcFilters.sdrId) {
        query = query.eq('sales_development', rpcFilters.sdrId);
      }
      if (rpcFilters.source) {
        query = query.eq('deals_source', rpcFilters.source);
      }
      if (rpcFilters.channel) {
        query = query.eq('deals_channel', rpcFilters.channel);
      }
      if (rpcFilters.createdFrom) {
        query = query.gte('created_at', rpcFilters.createdFrom.toISOString());
      }
      if (rpcFilters.createdTo) {
        query = query.lte('created_at', rpcFilters.createdTo.toISOString());
      }

      const { data, error } = await query;
      if (error) {
        console.error('[PipelineMetrics] Error fetching deals for aging:', error);
        throw error;
      }
      console.log('[PipelineMetrics] Deals for aging loaded:', data?.length || 0);
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Handler to retry deals for aging query
  const handleRetryAgingQuery = () => {
    queryClient.invalidateQueries({ queryKey: ['deals-for-aging', rpcFilters] });
  };

  // Deal Aging data
  const { dealsWithAging, bucketData, loading: agingLoading } = useDealAging(dealsForAging);
  
  // Deals without scheduled calls
  const { dataByConsultant, loading: callsLoading } = useDealsWithoutScheduledCalls(
    dealsForAging,
    users,
    { consultantFilter, sdrFilter }
  );

  // Filter users by role for dropdowns
  const salesConsultants = useMemo(() => {
    return users.filter(u => {
      const roleCode = u.system_role?.code;
      return u.active && (
        roleCode === 'sales_consultant' ||
        roleCode === 'sales_manager' ||
        roleCode === 'sales_director'
      );
    });
  }, [users]);

  const sdrs = useMemo(() => {
    return users.filter(u => {
      const roleCode = u.system_role?.code;
      return u.active && roleCode === 'sales_dev';
    });
  }, [users]);

  const hasActiveFilters = consultantFilter !== 'all' || sdrFilter !== 'all' || sourceFilter !== 'all' || channelFilter !== 'all' || createdDateFilter !== 'all';

  const clearFilters = () => {
    onConsultantFilterChange?.('all');
    onSdrFilterChange?.('all');
    onSourceFilterChange?.('all');
    onChannelFilterChange?.('all');
    onCreatedDateFilterChange?.('all');
    onCreatedDateRangeChange?.(null);
  };

  const handleCreatedDateFilterChange = (value) => {
    onCreatedDateFilterChange?.(value);
    const now = new Date();
    
    if (value === 'all') {
      onCreatedDateRangeChange?.(null);
    } else if (value === 'today') {
      onCreatedDateRangeChange?.({ from: getLocalDayStart(now), to: getLocalDayEnd(now) });
    } else if (value === 'yesterday') {
      const yesterday = subDays(now, 1);
      onCreatedDateRangeChange?.({ from: getLocalDayStart(yesterday), to: getLocalDayEnd(yesterday) });
    } else if (value === 'this_week') {
      onCreatedDateRangeChange?.({ from: getLocalWeekStart(now), to: getLocalDayEnd(now) });
    } else if (value === 'this_month') {
      onCreatedDateRangeChange?.({ from: getLocalMonthStart(now), to: getLocalDayEnd(now) });
    }
  };

  const handleStageClick = (stage) => {
    const normalized = String(stage).toLowerCase();
    const cfg = STAGE_CONFIG[normalized] ?? { label: normalized, color: 'hsl(var(--muted))' };
    setSelectedStage(normalized);
    setModalTitle(t('pipelineMetrics.modal.dealsInStage', { stage: cfg.label }));
    const stageCount = metrics?.stage_counts?.find(s => s.stage === normalized)?.count || 0;
    setModalSubtitle(t('pipelineMetrics.modal.dealsInStageCount', { count: stageCount }));
    setModalDeals([]);
    setModalOpen(true);
  };

  // Get stage counts from RPC data
  const getStageCounts = () => {
    const counts = {};
    STAGE_ORDER.forEach(stage => counts[stage] = 0);
    metrics?.stage_counts?.forEach(s => {
      counts[s.stage] = s.count;
    });
    return counts;
  };

  const stageCounts = getStageCounts();
  const activeStages = STAGE_ORDER.slice(0, 5);
  const totalActive = activeStages.reduce((sum, stage) => sum + stageCounts[stage], 0);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  // Get closing velocity data from RPC
  const wonVelocity = metrics?.closing_velocity?.find(v => v.stage === 'WON' || v.stage === 'won') || { avg_days: 0, median_days: 0, count: 0 };
  const lostVelocity = metrics?.closing_velocity?.find(v => v.stage === 'LOST' || v.stage === 'lost') || { avg_days: 0, median_days: 0, count: 0 };

  // Get stage time metrics from RPC
  const stageTimeMetrics = useMemo(() => {
    if (!metrics?.stage_time) return [];
    return activeStages.map(stage => {
      const stageData = metrics.stage_time.find(s => s.stage === stage) || { avg_days: 0, count: 0 };
      return {
        stage,
        avgDays: Math.round(stageData.avg_days || 0),
        count: stageData.count || 0,
      };
    });
  }, [metrics?.stage_time, activeStages]);

  // Win/loss rates
  const totalClosed = stageCounts.won + stageCounts.lost;
  const winRate = totalClosed > 0 ? Math.round((stageCounts.won / totalClosed) * 100) : 0;
  const lossRate = totalClosed > 0 ? Math.round((stageCounts.lost / totalClosed) * 100) : 0;

  // Daily deals chart config
  const chartConfig = {
    count: {
      label: t('salesMetrics.deals'),
      color: 'hsl(var(--primary))',
    },
  };

  // Bar click for daily deals
  const handleBarClick = (data) => {
    if (!data?.date) return;
    setModalTitle(t('pipelineMetrics.modal.dealsCreatedOn', { date: format(parseISO(data.date), 'dd/MM/yyyy') }));
    setModalSubtitle(t('pipelineMetrics.modal.dealsCreatedOnCount', { count: data.count }));
    setSelectedStage(null);
    setModalDeals([]);
    setModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-muted-foreground">{t('admin.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-destructive">
          Error loading pipeline metrics: {error.message}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Sales Consultant Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('pipelineMetrics.filters.salesConsultant')}</Label>
              <Select value={consultantFilter} onValueChange={onConsultantFilterChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('pipelineMetrics.filters.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('pipelineMetrics.filters.all')}</SelectItem>
                  <SelectItem value="unassigned">{t('pipelineMetrics.filters.unassigned')}</SelectItem>
                  {salesConsultants.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.display_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* SDR Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('pipelineMetrics.filters.sdr')}</Label>
              <Select value={sdrFilter} onValueChange={onSdrFilterChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('pipelineMetrics.filters.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('pipelineMetrics.filters.all')}</SelectItem>
                  <SelectItem value="unassigned">{t('pipelineMetrics.filters.unassigned')}</SelectItem>
                  {sdrs.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.display_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('pipelineMetrics.filters.source')}</Label>
              <Select value={sourceFilter} onValueChange={onSourceFilterChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('pipelineMetrics.filters.allSources')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('pipelineMetrics.filters.allSources')}</SelectItem>
                  {SOURCE_TYPES.map(source => (
                    <SelectItem key={source.value} value={source.value}>
                      {t(source.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Channel Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('pipelineMetrics.filters.channel')}</Label>
              <Select value={channelFilter} onValueChange={onChannelFilterChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('pipelineMetrics.filters.allChannels')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('pipelineMetrics.filters.allChannels')}</SelectItem>
                  {CHANNEL_TYPES.map(channel => (
                    <SelectItem key={channel.value} value={channel.value}>
                      {t(channel.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Created Date Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('pipelineMetrics.filters.createdDate')}</Label>
              <Select value={createdDateFilter} onValueChange={handleCreatedDateFilterChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('pipelineMetrics.dateOptions.allDates')} />
                </SelectTrigger>
                <SelectContent>
                  {CREATED_DATE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Created Date Range */}
            {createdDateFilter === 'custom' && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t('pipelineMetrics.filters.customRangeLabel')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !createdDateRange && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {createdDateRange?.from ? (
                        createdDateRange.to ? (
                          <>
                            {format(createdDateRange.from, "dd/MM")} - {format(createdDateRange.to, "dd/MM")}
                          </>
                        ) : (
                          format(createdDateRange.from, "dd/MM/yyyy")
                        )
                      ) : (
                        <span>{t('pipelineMetrics.filters.select')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={createdDateRange?.from}
                      selected={createdDateRange}
                      onSelect={onCreatedDateRangeChange}
                      numberOfMonths={2}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Clear Filters */}
            <div className="flex items-end">
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full">
                  <X className="h-4 w-4 mr-1" />
                  {t('pipelineMetrics.filters.clear')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Funnel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            {t('pipelineMetrics.funnel.title')}
            {hasActiveFilters && (
              <span className="text-xs font-normal text-muted-foreground ml-2">
                {t('pipelineMetrics.funnel.filtered')}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Active Stages Flow */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            {activeStages.map((stage, index) => (
              <React.Fragment key={stage}>
                <div className="flex flex-col items-center">
                  <div 
                    className="w-24 h-20 rounded-lg flex flex-col items-center justify-center shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
                    style={{ 
                      backgroundColor: `${STAGE_CONFIG[stage].color}15`,
                      borderColor: STAGE_CONFIG[stage].color,
                    }}
                    onClick={() => handleStageClick(stage)}
                  >
                    <span className="text-2xl font-bold" style={{ color: STAGE_CONFIG[stage].color }}>
                      {stageCounts[stage]}
                    </span>
                    <span className="text-xs text-muted-foreground">{STAGE_CONFIG[stage].label}</span>
                  </div>
                </div>
                {index < activeStages.length - 1 && (
                  <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* WON and LOST */}
          <div className="flex justify-center gap-8 pt-4 border-t">
            <div className="flex items-center gap-3">
              <div 
                className="w-20 h-16 rounded-lg flex flex-col items-center justify-center shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
                style={{ 
                  backgroundColor: 'hsl(var(--success) / 0.1)',
                  borderColor: 'hsl(var(--success))',
                }}
                onClick={() => handleStageClick('won')}
              >
                <span className="text-xl font-bold text-green-600">{stageCounts.won}</span>
                <span className="text-xs text-muted-foreground font-medium">{STAGE_CONFIG.won.label}</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <TrendingUp className="h-4 w-4 text-green-600 inline mr-1" />
                {t('pipelineMetrics.funnel.winRate', { percent: winRate })}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div 
                className="w-20 h-16 rounded-lg flex flex-col items-center justify-center shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
                style={{ 
                  backgroundColor: 'hsl(var(--destructive) / 0.1)',
                  borderColor: 'hsl(var(--destructive))',
                }}
                onClick={() => handleStageClick('lost')}
              >
                <span className="text-xl font-bold text-destructive">{stageCounts.lost}</span>
                <span className="text-xs text-muted-foreground font-medium">{STAGE_CONFIG.lost.label}</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <TrendingDown className="h-4 w-4 text-destructive inline mr-1" />
                {t('pipelineMetrics.funnel.lossRate', { percent: lossRate })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Time Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5 text-primary" />
            {t('pipelineMetrics.timeMetrics.title')}
            {hasActiveFilters && (
              <span className="text-xs font-normal text-muted-foreground ml-2">
                {t('pipelineMetrics.funnel.filtered')}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Time by Stage */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {t('pipelineMetrics.timeMetrics.timeByStage')}
              <span className="text-xs">({t('pipelineMetrics.timeMetrics.avgDaysSinceCreation')})</span>
            </h4>
            <div className="space-y-3">
              {stageTimeMetrics.map((item) => {
                const maxDays = Math.max(...stageTimeMetrics.map(s => s.avgDays), 1);
                const barWidth = (item.avgDays / maxDays) * 100;
                const isWarning = item.avgDays >= 7 && item.avgDays < 14;
                const isCritical = item.avgDays >= 14;
                
                const cfg = STAGE_CONFIG[item.stage] ?? { label: item.stage, color: 'hsl(var(--muted))' };
                return (
                  <div 
                    key={item.stage}
                    className="cursor-pointer hover:bg-accent/50 rounded-lg p-2 -mx-2 transition-colors"
                    onClick={() => handleStageClick(item.stage)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span 
                          className="text-sm font-medium"
                          style={{ color: cfg.color }}
                        >
                          {cfg.label}
                        </span>
                        {isCritical && <AlertTriangle className="h-4 w-4 text-destructive" />}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className={cn(
                          "font-semibold",
                          isCritical && "text-destructive",
                          isWarning && "text-yellow-600",
                          !isWarning && !isCritical && "text-green-600"
                        )}>
                          {item.avgDays} {t('pipelineMetrics.timeMetrics.days')}
                        </span>
                        <span className="text-muted-foreground">
                          ({item.count} {t('pipelineMetrics.timeMetrics.deals')})
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={cn(
                          "h-full rounded-full transition-all",
                          isCritical && "bg-destructive",
                          isWarning && "bg-yellow-500",
                          !isWarning && !isCritical && "bg-green-500"
                        )}
                        style={{ width: `${Math.max(barWidth, 2)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Closing Velocity */}
          <div className="pt-4 border-t">
            <h4 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {t('pipelineMetrics.timeMetrics.closingVelocity')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Won Deals */}
              <div 
                className="p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow"
                style={{ 
                  backgroundColor: 'hsl(var(--success) / 0.05)',
                  borderColor: 'hsl(var(--success) / 0.3)'
                }}
                onClick={() => handleStageClick('won')}
              >
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-700">{t('pipelineMetrics.timeMetrics.wonDeals')}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('pipelineMetrics.timeMetrics.avgDays')}</p>
                    <p className="text-2xl font-bold text-green-600">{wonVelocity.avg_days || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('pipelineMetrics.timeMetrics.days')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('pipelineMetrics.timeMetrics.medianDays')}</p>
                    <p className="text-2xl font-bold text-green-600">{Number(wonVelocity.median_days || 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">{t('pipelineMetrics.timeMetrics.days')}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('pipelineMetrics.timeMetrics.validDeals', { count: wonVelocity.count || 0 })}
                </p>
              </div>

              {/* Lost Deals */}
              <div 
                className="p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow"
                style={{ 
                  backgroundColor: 'hsl(var(--destructive) / 0.05)',
                  borderColor: 'hsl(var(--destructive) / 0.3)'
                }}
                onClick={() => handleStageClick('lost')}
              >
                <div className="flex items-center gap-2 mb-3">
                  <XCircle className="h-5 w-5 text-destructive" />
                  <span className="font-medium text-destructive">{t('pipelineMetrics.timeMetrics.lostDeals')}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('pipelineMetrics.timeMetrics.avgDays')}</p>
                    <p className="text-2xl font-bold text-destructive">{lostVelocity.avg_days || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('pipelineMetrics.timeMetrics.days')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('pipelineMetrics.timeMetrics.medianDays')}</p>
                    <p className="text-2xl font-bold text-destructive">{Number(lostVelocity.median_days || 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">{t('pipelineMetrics.timeMetrics.days')}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('pipelineMetrics.timeMetrics.validDeals', { count: lostVelocity.count || 0 })}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Daily Deals Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            {t('pipelineMetrics.charts.dealsCreatedPerDay')}
            <span className="text-xs font-normal text-muted-foreground ml-2">
              {t('pipelineMetrics.charts.clickBarForDetails')}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {metrics?.daily_deals && metrics.daily_deals.length > 0 ? (
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <BarChart data={metrics.daily_deals.map(d => ({
                ...d,
                label: format(parseISO(d.date), 'dd/MM'),
              }))}>
                <XAxis 
                  dataKey="label" 
                  tickLine={false} 
                  axisLine={false}
                  fontSize={12}
                />
                <YAxis 
                  tickLine={false} 
                  axisLine={false}
                  fontSize={12}
                  allowDecimals={false}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar 
                  dataKey="count" 
                  fill="hsl(var(--primary))" 
                  radius={[4, 4, 0, 0]}
                  name={t('salesMetrics.deals')}
                  cursor="pointer"
                  onClick={(data) => handleBarClick(data)}
                />
              </BarChart>
            </ChartContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              {t('pipelineMetrics.charts.noDealsInPeriod')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error state for dealsForAging query */}
      {dealsForAgingError && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="font-medium text-destructive">
                    {t('pipelineMetrics.aging.errorLoading', 'Erro ao carregar deals para Aging')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {dealsForAgingError.message || 'Erro desconhecido'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRetryAgingQuery}
                disabled={dealsForAgingFetching}
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", dealsForAgingFetching && "animate-spin")} />
                {t('common.retry', 'Tentar novamente')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state message when no deals found */}
      {!dealsForAgingError && !dealsLoading && dealsForAging.length === 0 && createdDateFilter !== 'all' && (
        <Card className="border-warning/50 bg-warning/5">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-warning" />
              <div>
                <p className="font-medium text-warning">
                  {t('pipelineMetrics.aging.noDealsForPeriod', 'Nenhum deal ativo encontrado para este período')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {createdDateRange?.from && createdDateRange?.to 
                    ? `${format(createdDateRange.from, 'dd/MM/yyyy')} - ${format(createdDateRange.to, 'dd/MM/yyyy')}`
                    : t('pipelineMetrics.aging.checkFilters', 'Verifique os filtros selecionados')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deal Aging Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Aging Chart - Horizontal Bars by Bucket */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-5 w-5 text-primary" />
              {t('pipelineMetrics.aging.byLastContact')}
              {hasActiveFilters && (
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  {t('pipelineMetrics.funnel.filtered')}
                </span>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {t('pipelineMetrics.aging.daysSinceLastContact')}
            </p>
          </CardHeader>
          <CardContent>
            <DealAgingChart 
              bucketData={bucketData}
              onBucketClick={(bucketDeals, bucketLabel) => {
                setModalTitle(t('pipelineMetrics.modal.dealsBucket', { bucket: bucketLabel }));
                setModalSubtitle(t('pipelineMetrics.modal.dealsBucketCount', { count: bucketDeals.length, bucket: bucketLabel }));
                setSelectedStage(null);
                setModalDeals(bucketDeals);
                setModalOpen(true);
              }}
            />
          </CardContent>
        </Card>

        {/* Aging List - Individual Deals */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-5 w-5 text-primary" />
              {t('pipelineMetrics.aging.dealListByContact')}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {t('pipelineMetrics.aging.orderedByCritical')}
            </p>
          </CardHeader>
          <CardContent>
            <DealAgingList 
              deals={dealsWithAging}
              loading={agingLoading || dealsLoading}
            />
          </CardContent>
        </Card>
      </div>

      {/* New Section: Deals without Calls & Deals by Creation Age */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deals without scheduled calls by consultant */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <PhoneOff className="h-5 w-5 text-destructive" />
              {t('pipelineMetrics.aging.dealsWithoutCall')}
              {hasActiveFilters && (
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  {t('pipelineMetrics.funnel.filtered')}
                </span>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {t('pipelineMetrics.aging.byConsultant')}
            </p>
          </CardHeader>
          <CardContent>
            <DealsWithoutScheduledCallsChart
              data={dataByConsultant}
              loading={callsLoading || dealsLoading}
              onBarClick={(barDeals, consultantName) => {
                setModalTitle(t('pipelineMetrics.modal.dealsWithoutCallTitle', { name: consultantName }));
                setModalSubtitle(t('pipelineMetrics.modal.dealsWithoutCallCount', { count: barDeals.length }));
                setSelectedStage(null);
                setModalDeals(barDeals);
                setModalOpen(true);
              }}
            />
          </CardContent>
        </Card>

        {/* Deals by creation age */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarIcon2 className="h-5 w-5 text-primary" />
              {t('pipelineMetrics.aging.dealsByCreation')}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {t('pipelineMetrics.aging.orderedByOldest')}
            </p>
          </CardHeader>
          <CardContent>
            <DealAgingByCreationList
              deals={dealsForAging}
              users={users}
              loading={dealsLoading}
            />
          </CardContent>
        </Card>
      </div>

      {/* Funnel Analytics Section */}
      <FunnelAnalyticsSection 
        businessUnit={businessUnitFilter !== 'all' ? businessUnitFilter : null}
        dateFrom={createdDateRange?.from}
        dateTo={createdDateRange?.to}
      />

      {/* Deals Detail Modal */}
      <DealsDetailModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={modalTitle}
        subtitle={modalSubtitle}
        deals={selectedStage ? (stageDealsData || []) : modalDeals}
      />
    </div>
  );
}
